import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, CalendarDays, Hash, UserPlus, UserMinus, TrendingDown, Plus, RefreshCcw, Sparkles, Sword } from "lucide-react";
import { formatNumber } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

type StakeEvent = {
  id: number;
  provisioner_address: string;
  event_type: 'stake' | 'unstake' | 'new_node' | 'node_exit' | 'large_stake_increase' | 'large_stake_decrease' | 'profit_taking' | 'restake' | 'first_time_node';
  previous_stake_amount: number;
  new_stake_amount: number;
  change_amount: number;
  timestamp: string;
};

const getEventDisplayInfo = (event: StakeEvent) => {
  switch (event.event_type) {
    case 'restake':
      return {
        icon: <RefreshCcw className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />,
        title: 'Re-stake Event',
        color: 'text-purple-600',
        description: `Re-staked to ${formatNumber(event.new_stake_amount)} DUSK`
      };
    case 'first_time_node':
      return {
        icon: <Sparkles className="h-5 w-5 text-cyan-500 mt-1 flex-shrink-0" />,
        title: 'First-Time Node',
        color: 'text-cyan-600',
        description: `Joined with ${formatNumber(event.new_stake_amount)} DUSK`
      };
    case 'new_node':
      return {
        icon: <UserPlus className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />,
        title: 'Node Rejoined',
        color: 'text-green-600',
        description: `Returned with ${formatNumber(event.new_stake_amount)} DUSK`
      };
    case 'node_exit':
      return {
        icon: <UserMinus className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />,
        title: 'Node Left Network',
        color: 'text-red-600',
        description: `Removed ${formatNumber(event.previous_stake_amount)} DUSK`
      };
    case 'large_stake_increase':
      return {
        icon: <Plus className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />,
        title: 'Large Stake Increase',
        color: 'text-green-600',
        description: `+${formatNumber(event.change_amount)} DUSK`
      };
    case 'large_stake_decrease':
      return {
        icon: <Sword className="h-5 w-5 text-orange-500 mt-1 flex-shrink-0" />,
        title: 'Node Slashed',
        color: 'text-orange-600',
        description: `-${formatNumber(event.change_amount)} DUSK`
      };
    case 'profit_taking':
      return {
        icon: <TrendingDown className="h-5 w-5 text-blue-500 mt-1 flex-shrink-0" />,
        title: 'Claim Rewards',
        color: 'text-blue-600',
        description: `Withdrew ${formatNumber(Math.abs(event.previous_stake_amount - event.new_stake_amount))} DUSK`
      };
    default:
      return {
        icon: <Hash className="h-5 w-5 text-gray-500 mt-1 flex-shrink-0" />,
        title: 'Network Event',
        color: 'text-gray-600',
        description: `${formatNumber(event.change_amount)} DUSK`
      };
  }
};

interface NodeEventHistoryProps {
  address: string;
}

export function NodeEventHistory({ address }: NodeEventHistoryProps) {
  const { data: events, isLoading, error } = useQuery({
    queryKey: ["nodeStakeEvents", address],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stake_events')
        .select('*')
        .eq('provisioner_address', address)
        .order('timestamp', { ascending: false })
        .limit(20);

      if (error) throw error;
      
      return data?.map(event => ({
        ...event,
        event_type: event.event_type as StakeEvent['event_type'],
        change_amount: event.change_amount || Math.abs(event.new_stake_amount - event.previous_stake_amount)
      })) as StakeEvent[] || [];
    },
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Node Event History</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3">
              <Skeleton className="h-6 w-6 rounded-full" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-3 w-[200px]" />
                <Skeleton className="h-3 w-[150px]" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Node Event History</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load node events: {error.message}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Node Event History</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">No recorded events for this node yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl font-bold">Node Event History</CardTitle>
        <p className="text-sm text-muted-foreground">Complete activity timeline for this node</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {events.map((event) => {
            const eventInfo = getEventDisplayInfo(event);
            
            return (
              <div key={event.id} className="flex items-start space-x-3 pb-3 border-b last:border-b-0 last:pb-0">
                {eventInfo.icon}
                <div className="flex-grow min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm font-semibold ${eventInfo.color}`}>
                      {eventInfo.title}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center whitespace-nowrap">
                      <CalendarDays className="h-3 w-3 mr-1" />
                      {new Date(event.timestamp).toLocaleDateString()} {new Date(event.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-sm mt-1">
                    {eventInfo.description}
                  </p>
                  {event.event_type !== 'node_exit' && event.event_type !== 'profit_taking' && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Stake: {formatNumber(event.new_stake_amount)} DUSK
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
